import { getAnalyticsShopKey, isDupeShopKey, isValidUrl } from './helpers'

/**
 * Canonical merchant identity for analytics events (`merchant_key`).
 *
 * Born out of the 2026-08 affiliate attribution audit
 * (docs/references/affiliate-attribution-audit-2026-08.md): outbound-click
 * events carried four different merchant property names (`merchant`,
 * `retailer`, `shopKey`, `shop_key`) with four value shapes (origin URL,
 * display name, bare brand word, nothing), which made EPC-by-merchant a
 * manual investigation. `deriveMerchantKey` reads whatever hint an event
 * payload happens to carry and resolves it to ONE format.
 *
 * Contract:
 * - The value is always a normalized analytics shop origin
 *   (`https://wayfair.com` — see getAnalyticsShopKey, which strips repeated
 *   www./m./secure. prefixes) or absent. Never free text, never a slug —
 *   a consumer can rely on parsing it as a URL.
 * - Derivation is ADDITIVE. Existing properties are never renamed or
 *   rewritten; live PostHog experiment metrics filter on the legacy keys
 *   (see the warning in ProductListCard.tsx) so those must keep flowing.
 * - Dupe's own redirect domains (dupe.com / dupe.link) never count as a
 *   merchant — encrypted /redirect links fall through to the next hint.
 */

/**
 * Payload keys that may carry a merchant hint, in priority order: explicit
 * shop keys first, then product/outbound URLs, then name-ish fields that are
 * sometimes domains (`merchant` on the gift guide is a bare word and won't
 * parse; on other emitters it can be a domain and will).
 */
const MERCHANT_HINT_KEYS = [
  'merchant_key',
  'shopKey',
  'shop_key',
  'product_url',
  'productUrl',
  'outbound_url',
  'link',
  'linkUrl',
  'merchant',
  'retailer',
  'retailerName',
] as const

/**
 * Affiliate tracking wrappers whose hostname masks the real retailer. The
 * destination survives in the wrapped URL's query string, so this MUST run
 * on the raw link — once a URL is reduced to its origin the destination is
 * gone and unmasking is impossible. (Tinybird's resolveShopKey historically
 * normalized first, which made its Target check dead code and keyed those
 * rows under the wrapper origin `https://c.next2.io`; it now shares this
 * helper, so both merchant_key and new Tinybird shop_key rows emit
 * `https://target.com`.)
 *
 * `c.next2.io` (Ad Net) is a SHARED wrapper — one hostname, many advertisers,
 * disambiguated only by the embedded destination link (the `sid` param is
 * our own publisher id, not a per-merchant one; see bbb.ts). BBB joined
 * Target here when its redirect moved off AdMedia onto Ad Net — add any
 * future Ad Net merchant's `www.<domain>` the same way, not as a new
 * hardcoded branch.
 *
 * adMarketplace AMP click URLs (`bridge.<colo>.admarketplace.net/ct?encp=…`)
 * are also wrappers, but the destination is encrypted and AMP Discover ads
 * can point at any advertiser — so they can never be unmasked, only skipped.
 * Post-merge verification of the original rollout found ~1K events/day keyed
 * `https://bridge.ric1.admarketplace.net` before this hostname was added.
 */
const TRACKING_WRAPPER_RE =
  /^https?:\/\/(c\.next2\.io|c1\.intentxredir\.com|bridge\.[a-z0-9-]+\.admarketplace\.net)/i

/**
 * Merchants that can be embedded inside a next2.io/intentxredir wrapper,
 * keyed by the bare `www.<domain>` marker to look for. Matched against the
 * RAW (possibly percent-encoded) link — `://` gets encoded by the wrapper
 * but the hostname doesn't, so the marker deliberately omits the scheme.
 */
const NEXT2_IO_WRAPPED_MERCHANTS: Array<{ marker: string; origin: string }> = [
  { marker: 'www.target.com', origin: 'https://www.target.com' },
  {
    marker: 'www.bedbathandbeyond.com',
    origin: 'https://www.bedbathandbeyond.com',
  },
]

/**
 * Resolve an affiliate tracking-wrapper link to the real merchant's
 * analytics origin, or null when the value isn't a wrapper / can't be
 * unmasked. Shared by deriveMerchantKey and Tinybird row building.
 */
export const unmaskTrackingWrapperOrigin = (raw: string): string | null => {
  if (!TRACKING_WRAPPER_RE.test(raw)) return null

  const match = NEXT2_IO_WRAPPED_MERCHANTS.find((m) => raw.includes(m.marker))
  return match ? getAnalyticsShopKey(match.origin) : null
}

export const deriveMerchantKey = (
  properties: Record<string, unknown> | null | undefined
): string | null => {
  if (!properties) return null

  for (const key of MERCHANT_HINT_KEYS) {
    const raw = properties[key]
    if (typeof raw !== 'string') continue
    const trimmed = raw.trim()
    if (!trimmed) continue

    const unmasked = unmaskTrackingWrapperOrigin(trimmed)
    if (unmasked) return unmasked

    // A wrapper value that couldn't be unmasked is a tracking middleman, not
    // a merchant. This bites when shopKey was pre-derived via getShopKey()
    // (origin only — the destination in the query string is already gone):
    // skip the hint so a later one, like the full link, can still unmask.
    if (TRACKING_WRAPPER_RE.test(trimmed)) continue

    // Rejects bare brand words ('Wayfair', 'sidepanel') — only values with a
    // registrable hostname qualify, keeping the origin-only contract.
    if (!isValidUrl(trimmed)) continue

    const merchantKey = getAnalyticsShopKey(trimmed)
    if (!merchantKey || isDupeShopKey(merchantKey)) continue

    return merchantKey
  }

  return null
}
